import { useRef, useState, useCallback } from 'react'
import html2canvas from 'html2canvas'
import type { ProjectProfile } from '@/lib/types'
import { CHAIN_LABELS, toProjectSlug } from '@/lib/types'

const MANDOX_LOGO = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'

// ─── Off-screen card that gets captured ───────────────────────────────────────
function ShareCardVisual({
  cardRef,
  project,
}: {
  cardRef: React.RefObject<HTMLDivElement>
  project: ProjectProfile
}) {
  const audit   = project.audit!
  const passed  = audit.verdict === 'passed'
  const score   = audit.score ?? 0

  const scoreColor =
    score >= 80 ? '#22c55e' :
    score >= 60 ? '#1ad6c4' :
    score >= 40 ? '#f59e0b' : '#ef4444'

  const verdictColor  = passed ? '#34d399' : '#f87171'
  const verdictLabel  = passed ? 'AUDIT PASSED' : 'AUDIT FAILED'
  const verdictSymbol = passed ? '✓' : '✗'

  // SVG score ring values
  const R   = 38
  const CIR = 2 * Math.PI * R
  const pct = Math.min(100, Math.max(0, score)) / 100
  const dash = CIR * pct
  const gap  = CIR - dash

  return (
    <div
      ref={cardRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        top:  '-9999px',
        width:  '1280px',
        height: '720px',
        background: '#020608',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Grid overlay */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(26,214,196,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(26,214,196,0.06) 1px, transparent 1px)`,
        backgroundSize: '64px 64px',
      }} />

      {/* Turquoise glow — left */}
      <div style={{
        position: 'absolute', left: -80, top: '50%', transform: 'translateY(-50%)',
        width: 500, height: 500,
        background: 'radial-gradient(circle, rgba(26,214,196,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Glow — verdict color top-right */}
      <div style={{
        position: 'absolute', right: -60, top: -60,
        width: 420, height: 420,
        background: `radial-gradient(circle, ${verdictColor}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 6,
        background: `linear-gradient(90deg, #1ad6c4, ${verdictColor})`,
      }} />

      {/* Mandox logo — top right */}
      <img
        src={MANDOX_LOGO}
        alt="Mandox"
        style={{
          position: 'absolute', top: 40, right: 56,
          height: 60, width: 'auto',
          objectFit: 'contain',
        }}
        crossOrigin="anonymous"
      />

      {/* ── Left content area ── */}
      <div style={{ position: 'absolute', left: 96, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '58%' }}>

        {/* Project identity row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 44 }}>
          {/* Project logo */}
          <div style={{
            width: 120, height: 120, borderRadius: 24, overflow: 'hidden', flexShrink: 0,
            background: project.logoUrl ? 'transparent' : '#0a1518',
            border: '3px solid rgba(26,214,196,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#1ad6c4', fontSize: 42, fontWeight: 700,
          }}>
            {project.logoUrl
              ? <img src={project.logoUrl} alt={project.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
              : project.symbol.slice(0, 2)
            }
          </div>
          <div>
            <div style={{ fontSize: 44, fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
              {project.name}
            </div>
            <div style={{ fontSize: 24, color: '#6a7682', marginTop: 8, letterSpacing: '0.04em' }}>
              ${project.symbol} &nbsp;·&nbsp; {CHAIN_LABELS[project.chain]}
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
          <span style={{
            fontSize: 30, fontWeight: 800, letterSpacing: '0.12em',
            color: verdictColor, textTransform: 'uppercase',
          }}>
            {verdictLabel}
          </span>
          <span style={{
            fontSize: 38, fontWeight: 900, color: verdictColor,
            lineHeight: 1,
          }}>
            {verdictSymbol}
          </span>
        </div>

        {/* Score bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            height: 8, width: 300, borderRadius: 99,
            background: 'rgba(255,255,255,0.07)', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${score}%`,
              background: `linear-gradient(90deg, #1ad6c4, ${scoreColor})`,
              borderRadius: 99,
            }} />
          </div>
          <span style={{ fontSize: 22, color: '#5a6470' }}>Security Score</span>
        </div>

        {/* Contract address */}
        <div style={{ marginTop: 28, fontSize: 20, color: '#3a4450', fontFamily: 'monospace', letterSpacing: '0.04em' }}>
          {project.contractAddress.slice(0, 16)}…{project.contractAddress.slice(-8)}
        </div>
      </div>

      {/* ── Score ring — right side ── */}
      <div style={{
        position: 'absolute', right: 96, top: '50%', transform: 'translateY(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
      }}>
        <svg width={210} height={210} viewBox="0 0 96 96">
          {/* Track */}
          <circle cx={48} cy={48} r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={7} />
          {/* Progress */}
          <circle
            cx={48} cy={48} r={R}
            fill="none"
            stroke={scoreColor}
            strokeWidth={7}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={CIR / 4}
            style={{ filter: `drop-shadow(0 0 6px ${scoreColor}88)` }}
          />
          {/* Score number */}
          <text x={48} y={44} textAnchor="middle" fill={scoreColor} fontSize={22} fontWeight={800} fontFamily="-apple-system,sans-serif">
            {score}
          </text>
          <text x={48} y={60} textAnchor="middle" fill="#4a5662" fontSize={9} fontFamily="-apple-system,sans-serif">
            /100
          </text>
        </svg>
        <div style={{ fontSize: 20, color: '#4a5662', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Score
        </div>
      </div>

      {/* ── Footer strip ── */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 72,
        borderTop: '1px solid rgba(26,214,196,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingLeft: 96, paddingRight: 96,
      }}>
        <span style={{ fontSize: 20, color: '#3a4450', letterSpacing: '0.06em' }}>
          AUDITED BY MANDOX AUDITS
        </span>
        <span style={{ fontSize: 20, color: '#1ad6c4', letterSpacing: '0.04em' }}>
          Mandox.io
        </span>
      </div>
    </div>
  )
}

// ─── Toast state (module-level, simple) ──────────────────────────────────────
let _setToast: ((msg: string | null) => void) | null = null

export function ShareToast() {
  const [msg, setMsg] = useState<string | null>(null)
  _setToast = setMsg

  if (!msg) return null
  return (
    <div
      onClick={() => setMsg(null)}
      style={{
        position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999,
        background: '#0d1f1e',
        border: '1px solid rgba(26,214,196,0.35)',
        borderRadius: 12,
        padding: '12px 20px',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        maxWidth: 360, width: 'calc(100vw - 40px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        cursor: 'pointer',
        animation: 'fadeInUp 0.2s ease',
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>📋</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1ad6c4', marginBottom: 3 }}>
          Tweet text copied!
        </div>
        <div style={{ fontSize: 12, color: '#8a9aaa', lineHeight: 1.5 }}>
          X is opening now — paste the image you just saved into the post, then paste the text.
        </div>
      </div>
    </div>
  )
}

function showToast(msg: string) {
  if (_setToast) {
    _setToast(msg)
    setTimeout(() => { if (_setToast) _setToast(null) }, 6000)
  }
}

// ─── Public hook ──────────────────────────────────────────────────────────────
export function useShareAudit(project: ProjectProfile) {
  const cardRef = useRef<HTMLDivElement>(null!)
  const [loading, setLoading] = useState(false)

  const share = useCallback(async () => {
    if (!cardRef.current) return
    setLoading(true)
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#020608',
        logging: false,
      })

      // Build the PNG file blob
      const dataUrl = canvas.toDataURL('image/png')
      const res     = await fetch(dataUrl)
      const blob    = await res.blob()
      const file    = new File([blob], `${project.symbol}-mandox-audit.png`, { type: 'image/png' })

      // Build tweet text
      const projectUrl = `https://Mandox.io/projects/${toProjectSlug(project.name)}`
      const tweetText  =
        `✅ ${project.name} ($${project.symbol}) has just passed a full smart contract audit by @Mandox_Audits.\n\n` +
        `🔐 Security Score: ${project.audit?.score ?? 0}/100\n` +
        `⛓ Chain: ${CHAIN_LABELS[project.chain]}\n\n` +
        `Audit your token. Earn trust on-chain.\n` +
        `👉 ${projectUrl}`

      // ── Mobile: Web Share API (opens X app with image attached) ──────────────
      const canShareFiles =
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })

      if (canShareFiles) {
        await navigator.share({
          files: [file],
          title: `${project.name} Audit — Mandox Audits`,
          text: tweetText,
        })
        return
      }

      // ── Desktop fallback ─────────────────────────────────────────────────────
      // 1. Download the image so they can attach it
      const a = document.createElement('a')
      a.href = dataUrl
      a.download = `${project.symbol}-mandox-audit.png`
      a.click()

      // 2. Copy tweet text to clipboard
      try { await navigator.clipboard.writeText(tweetText) } catch (_) {}

      // 3. Open X compose (text pre-filled via intent URL)
      const intentUrl =
        `https://x.com/intent/tweet?text=` +
        encodeURIComponent(
          `✅ ${project.name} ($${project.symbol}) has just passed a full smart contract audit by @Mandox_Audits.\n\n` +
          `🔐 Security Score: ${project.audit?.score ?? 0}/100\n` +
          `⛓ Chain: ${CHAIN_LABELS[project.chain]}\n\n` +
          `Audit your token. Earn trust on-chain.\n` +
          `👉 ${projectUrl}`
        )

      setTimeout(() => {
        window.open(intentUrl, '_blank', 'noopener,noreferrer')
      }, 500)

      // 4. Show clipboard toast
      showToast('clipboard')

    } finally {
      setLoading(false)
    }
  }, [project])

  const CardPortal = () => (
    <ShareCardVisual cardRef={cardRef} project={project} />
  )

  return { share, loading, CardPortal }
}
